import { ReactElement, ReactNode } from "react";
import type { ImportResultSummary, PipelineRow } from "./resultSummaryModel";
import { REVIEW_SECTION_ID } from "./ReviewLists";
import {
  PANEL,
  SECTION_TITLE,
  TABLE_CELL,
  TABLE_HEAD,
  TABLE_HEAD_RIGHT,
  TABLE_ROW,
} from "./staffSurface";

interface ImportResultSummaryProps {
  summary: ImportResultSummary;
}

interface MetricLinks {
  added?: string;
  updated?: string;
  deletable?: string;
  writeFailed?: string;
}

export default function ImportResultSummaryTables({
  summary,
}: ImportResultSummaryProps): ReactElement {
  const { pipeline } = summary;

  return (
    <section className={PANEL}>
      <h2 className={SECTION_TITLE}>Data Received</h2>
      <div className="overflow-x-auto">
        <table className="w-full min-w-[36rem] border-collapse text-sm text-gray-font">
          <thead>
            <tr>
              <th className={TABLE_HEAD}>&nbsp;</th>
              <th className={TABLE_HEAD_RIGHT}>Found</th>
              <th className={TABLE_HEAD_RIGHT}>New</th>
              <th className={TABLE_HEAD_RIGHT}>Updated</th>
              <th className={TABLE_HEAD_RIGHT}>Unchanged</th>
              <th className={TABLE_HEAD_RIGHT}>Deletable</th>
              <th className={TABLE_HEAD_RIGHT}>Write failed</th>
            </tr>
          </thead>
          <tbody>
            <PipelineTableRow
              label="Professionals"
              row={pipeline.professionals}
              links={{
                added: REVIEW_SECTION_ID.additions,
                updated: REVIEW_SECTION_ID.updates,
                deletable: REVIEW_SECTION_ID.deletions,
                writeFailed: REVIEW_SECTION_ID.writeFailures,
              }}
            />
          </tbody>
        </table>
      </div>
      <p className="mt-2 text-xs text-gray-500">
        Found is every professional the feed named. New, Updated, and Deletable
        open the matching review list. Deletable are the firm&apos;s
        professionals no feed entry answered for; nothing is deleted.
      </p>
    </section>
  );
}

function PipelineTableRow({
  label,
  row,
  links,
}: {
  label: string;
  row: PipelineRow;
  links?: MetricLinks;
}): ReactElement {
  return (
    <tr className={TABLE_ROW}>
      <td className={TABLE_CELL}>{label}</td>
      <MetricCell>{row.found}</MetricCell>
      <MetricCell sectionId={links?.added}>{row.added}</MetricCell>
      <MetricCell sectionId={links?.updated}>{row.updated ?? 0}</MetricCell>
      <MetricCell>{row.unchanged}</MetricCell>
      <MetricCell sectionId={links?.deletable}>{row.deletable ?? 0}</MetricCell>
      <MetricCell sectionId={links?.writeFailed}>{row.writeFailed}</MetricCell>
    </tr>
  );
}

function MetricCell({
  children,
  muted,
  sectionId,
}: {
  children: ReactNode;
  muted?: boolean;
  sectionId?: string;
}): ReactElement {
  const value =
    sectionId && !muted ? (
      <a
        href={`#${sectionId}`}
        className="text-secondary underline"
        onClick={() => {
          const section = document.getElementById(sectionId);
          if (section instanceof HTMLDetailsElement) {
            section.open = true;
          }
        }}
      >
        {children}
      </a>
    ) : (
      children
    );

  return (
    <td
      className={`${TABLE_CELL} text-right tabular-nums ${muted ? "text-gray-400" : ""}`}
    >
      {value}
    </td>
  );
}
